import { Hero } from '@/components/sections/Hero';
import { privacyPolicy } from '@/data/legal/privacy';
import { termsOfUse } from '@/data/legal/terms';
import { refundPolicy } from '@/data/legal/refund';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { useSeo } from '@/lib/seo';
import { cn } from '@/lib/cn';

type LegalType = 'privacy' | 'terms' | 'refund';

type Props = {
  type: LegalType;
};

const contentMap = {
  privacy: privacyPolicy,
  terms: termsOfUse,
  refund: refundPolicy,
};

export default function LegalPage({ type }: Props) {
  const content = contentMap[type];
  useSeo({
    title: content.title,
    description: `${content.title} - JUST SEE LTD.`,
  });

  return (
    <>
      <Hero
        eyebrow={`Legal · Updated ${content.lastUpdated}`}
        headline={content.title}
        align="centered"
      />

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.35fr_0.65fr] gap-14 lg:gap-20">
          <nav aria-label="Section navigation" className="lg:sticky lg:top-32 lg:self-start">
            <h2 className="eyebrow mb-6">Sections</h2>
            <ol className="flex flex-col gap-2">
              {content.sections.map((section, idx) => (
                <li key={section.heading}>
                  <a
                    href={`#section-${idx}`}
                    className={cn(
                      'text-sm font-sans text-ink-secondary hover:text-accent transition-colors flex gap-3',
                    )}
                  >
                    <span className="mono-meta text-ink-muted">{String(idx + 1).padStart(2, '0')}</span>
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="prose-custom text-ink-secondary">
            {content.sections.map((section, idx) => (
              <section key={section.heading} id={`section-${idx}`} className="mb-12">
                <h2 className="font-display text-2xl md:text-3xl font-medium text-ink-primary mb-5">
                  {section.heading}
                </h2>
                {section.body.map((para) => (
                  <p key={para.slice(0, 30)}>{para}</p>
                ))}
              </section>
            ))}
          </article>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
